import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  ObjectStoreExclusionRuleMatchTypeEnum,
  type ObjectStoreExclusionRule as ObjectExclusionRule,
} from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { validateRegex } from '@/components/form/validation/fieldValidation';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { dayjs } from '@/utils/dayjs';

export interface ObjectFiltersPanelProps {
  entity: ObjectExclusionRule;
  allRules: ObjectExclusionRule[];
  onSave: (rule: ObjectExclusionRule) => void;
  onClose: () => void;
}

export function ObjectFiltersPanel(props: ObjectFiltersPanelProps) {
  const [data, setData] = useState(props.entity);
  const [prefix, setPrefix] = useState(
    data.matchType === ObjectStoreExclusionRuleMatchTypeEnum.Prefix
      ? data.bucketPrefix || ''
      : ''
  );
  const [regex, setRegex] = useState(
    data.matchType === ObjectStoreExclusionRuleMatchTypeEnum.Regex
      ? data.bucketPrefix || ''
      : ''
  );

  const bucketPrefix = useMemo(() => {
    return data.matchType === ObjectStoreExclusionRuleMatchTypeEnum.Prefix
      ? prefix
      : regex;
  }, [data.matchType, prefix, regex]);

  const onSave = () => {
    props.onSave({
      ...data,
      bucketPrefix,
    });
  };

  const existingRule = props.allRules.find(
    (x) => x.id !== data.id && x.bucket === data.bucket
  );

  useEffect(() => {
    if (existingRule) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setData((state) => ({
        ...state,
        include: existingRule.include,
      }));
    }
  }, [existingRule]);

  return (
    <PanelWrapper
      header={{
        onClose: props.onClose,
        title: props.entity.id ? 'Edit filter' : 'Create filter',
        subtitle: `Set up a filter to include or exclude specific ${data.storeType === 'S3' ? 'S3' : 'Google Cloud Storage'} objects from being backed up.`,
      }}
      footer={{
        primaryButton: {
          text: props.entity.id ? 'Save' : 'Create Filter',
          onClick: onSave,
          'data-testid': 'save-button',
          disabled:
            !data.bucket ||
            (!bucketPrefix && !data.modificationDate) ||
            (data.matchType === ObjectStoreExclusionRuleMatchTypeEnum.Regex &&
              !validateRegex.validateInput(regex)),
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: props.onClose,
          'data-testid': 'cancel-button',
        },
      }}
    >
      <Stack
        paddingX='min(100px, 6vw)'
        paddingY='40px'
        className='h-full'
        gap='40px'
        divider={<Divider sx={{ borderStyle: 'dashed' }} />}
      >
        <Stack gap='40px'>
          <FormControl className='w-1/2'>
            <FormTextField
              title={
                <>
                  {'Bucket name (Set '}
                  <Box
                    component='code'
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.85em',
                      backgroundColor: 'var(--mui-palette-action-hover)',
                      borderRadius: '4px',
                      paddingX: '4px',
                      paddingY: '1px',
                    }}
                  >
                    *
                  </Box>
                  {' for all buckets)'}
                </>
              }
              placeholder='e.g. my-bucket'
              value={data.bucket}
              onChange={(event) => {
                setData({
                  ...data,
                  bucket: event.target.value,
                });
              }}
            />
          </FormControl>
          <FormControl className='w-1/2'>
            <FormLabel>Filter type</FormLabel>
            <FormControlLabel
              disabled={!!existingRule}
              checked={!!data.include}
              onChange={() => {
                setData({
                  ...data,
                  include: true,
                });
              }}
              control={<Radio className='pr-[12px]' />}
              label='Include'
            />
            <FormControlLabel
              disabled={!!existingRule}
              checked={!data.include}
              onChange={() => {
                setData({
                  ...data,
                  include: false,
                });
              }}
              control={<Radio className='pr-[12px]' />}
              label='Exclude'
            />
          </FormControl>
        </Stack>
        <Stack gap='40px'>
          <ToggleButtonGroup
            exclusive
            sx={{ width: 'max-content' }}
            value={data.matchType}
            onChange={(_, value) => {
              if (value === null) {
                return;
              }
              setData((state) => ({
                ...state,
                matchType: value,
              }));
            }}
          >
            <ToggleButton value={ObjectStoreExclusionRuleMatchTypeEnum.Prefix}>
              Prefix
            </ToggleButton>
            <ToggleButton value={ObjectStoreExclusionRuleMatchTypeEnum.Regex}>
              Regex
            </ToggleButton>
          </ToggleButtonGroup>
          <FormControl className='w-1/2'>
            {data.matchType === ObjectStoreExclusionRuleMatchTypeEnum.Prefix ? (
              <FormTextField
                title={`${data.include ? 'Include' : 'Exclude'} objects with prefix`}
                placeholder='path/in/bucket'
                value={prefix}
                onChange={(event) => {
                  setPrefix(event.target.value.replace(/^\/+/, ''));
                }}
              />
            ) : (
              <FormTextField
                title={`${data.include ? 'Include' : 'Exclude'} objects with regex`}
                placeholder='e.g. .*\\.csv$'
                value={regex}
                validation={validateRegex}
                onChange={(event) => {
                  setRegex(event.target.value);
                }}
              />
            )}
          </FormControl>
          <FormControl className='w-1/2'>
            <FormLabel>{`${data.include ? 'Include objects modified after' : 'Exclude objects modified before'} `}</FormLabel>
            <DatePicker
              value={
                data.modificationDate ? dayjs.utc(data.modificationDate) : null
              }
              onChange={(newValue) => {
                setData({
                  ...data,
                  modificationDate: newValue?.toDate() || null,
                });
              }}
            />
          </FormControl>
        </Stack>
      </Stack>
    </PanelWrapper>
  );
}
